import { alpha } from '@mui/material/styles';
import { svgIconClasses } from '@mui/material/SvgIcon';
import { typographyClasses } from '@mui/material/Typography';
import { buttonBaseClasses } from '@mui/material/ButtonBase';
import { chipClasses } from '@mui/material/Chip';
import { iconButtonClasses } from '@mui/material/IconButton';
import { gray, red, green, brand, orange } from '../getDesignTokens';

/* eslint-disable import/prefer-default-export */
export const dataDisplayCustomizations = {
  MuiList: {
    styleOverrides: {
      root: {
        padding: '8px',
        display: 'flex',
        flexDirection: 'column',
        gap: 0,
      },
    },
  },
  MuiListItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        [`& .${svgIconClasses.root}`]: {
          width: '1rem',
          height: '1rem',
          color: theme.palette.text.secondary,
        },
        [`& .${typographyClasses.root}`]: {
          fontWeight: 500,
        },
        [`& .${buttonBaseClasses.root}`]: {
          display: 'flex',
          gap: 8,
          padding: '2px 8px',
          borderRadius: theme.shape.borderRadius,
          opacity: 0.7,
          '&.Mui-selected': {
            opacity: 1,
            backgroundColor: alpha(theme.palette.action.selected, 0.3),
            [`& .${svgIconClasses.root}`]: {
              color: theme.palette.text.primary,
            },
            '&:focus-visible': {
              backgroundColor: alpha(theme.palette.action.selected, 0.3),
            },
            '&:hover': {
              backgroundColor: alpha(theme.palette.action.selected, 0.5),
            },
          },
          '&:focus-visible': {
            backgroundColor: 'transparent',
          },
        },
      }),
    },
  },
  MuiListItemText: {
    styleOverrides: {
      primary: ({ theme }) => ({
        fontSize: theme.typography.body2.fontSize,
        fontWeight: 500,
        lineHeight: theme.typography.body2.lineHeight,
      }),
      secondary: ({ theme }) => ({
        fontSize: theme.typography.caption.fontSize,
        lineHeight: theme.typography.caption.lineHeight,
      }),
    },
  },
  MuiListSubheader: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: 'transparent',
        padding: '4px 8px',
        fontSize: theme.typography.caption.fontSize,
        fontWeight: 500,
        lineHeight: theme.typography.caption.lineHeight,
      }),
    },
  },
  MuiListItemIcon: {
    styleOverrides: {
      root: {
        minWidth: 0,
      },
    },
  },
  MuiChip: {
    defaultProps: {
      size: 'small',
    },
    styleOverrides: {
      root: ({ theme }) => ({
        border: '1px solid',
        borderRadius: '999px',
        [`& .${chipClasses.label}`]: {
          fontWeight: 600,
        },
        variants: [
          {
            props: {
              color: 'default',
            },
            style: {
              borderColor: gray[200],
              backgroundColor: gray[100],
              [`& .${chipClasses.label}`]: {
                color: gray[500],
              },
              [`& .${chipClasses.icon}`]: {
                color: gray[500],
              },
              ...theme.applyStyles('dark', {
                borderColor: gray[700],
                backgroundColor: gray[800],
                [`& .${chipClasses.label}`]: {
                  color: gray[300],
                },
                [`& .${chipClasses.icon}`]: {
                  color: gray[300],
                },
              }),
            },
          },
          {
            props: {
              color: 'success',
            },
            style: {
              borderColor: green[200],
              backgroundColor: green[50],
              [`& .${chipClasses.label}`]: {
                color: green[500],
              },
              [`& .${chipClasses.icon}`]: {
                color: green[500],
              },
              ...theme.applyStyles('dark', {
                borderColor: green[800],
                backgroundColor: green[900],
                [`& .${chipClasses.label}`]: {
                  color: green[300],
                },
                [`& .${chipClasses.icon}`]: {
                  color: green[300],
                },
              }),
            },
          },
          {
            props: {
              color: 'primary',
            },
            style: {
              borderColor: brand[200],
              backgroundColor: brand[50],
              [`& .${chipClasses.label}`]: {
                color: brand[700],
              },
              [`& .${chipClasses.icon}`]: {
                color: brand[700],
              },
              ...theme.applyStyles('dark', {
                borderColor: brand[800],
                backgroundColor: brand[900],
                [`& .${chipClasses.label}`]: {
                  color: brand[300],
                },
                [`& .${chipClasses.icon}`]: {
                  color: brand[300],
                },
              }),
            },
          },
          {
            props: {
              color: 'warning',
            },
            style: {
              borderColor: orange[200],
              backgroundColor: orange[50],
              [`& .${chipClasses.label}`]: {
                color: orange[500],
              },
              [`& .${chipClasses.icon}`]: {
                color: orange[500],
              },
              ...theme.applyStyles('dark', {
                borderColor: orange[800],
                backgroundColor: orange[900],
                [`& .${chipClasses.label}`]: {
                  color: orange[300],
                },
                [`& .${chipClasses.icon}`]: {
                  color: orange[300],
                },
              }),
            },
          },
          {
            props: {
              color: 'error',
            },
            style: {
              borderColor: red[100],
              backgroundColor: red[50],
              [`& .${chipClasses.label}`]: {
                color: red[500],
              },
              [`& .${chipClasses.icon}`]: {
                color: red[500],
              },
              ...theme.applyStyles('dark', {
                borderColor: red[800],
                backgroundColor: red[900],
                [`& .${chipClasses.label}`]: {
                  color: red[200],
                },
                [`& .${chipClasses.icon}`]: {
                  color: red[300],
                },
              }),
            },
          },
          {
            props: { size: 'small' },
            style: {
              maxHeight: 20,
              [`& .${chipClasses.label}`]: {
                fontSize: theme.typography.caption.fontSize,
              },
              [`& .${svgIconClasses.root}`]: {
                fontSize: theme.typography.caption.fontSize,
              },
            },
          },
          {
            props: { size: 'medium' },
            style: {
              [`& .${chipClasses.label}`]: {
                fontSize: theme.typography.caption.fontSize,
              },
            },
          },
        ],
      }),
    },
  },
  MuiTablePagination: {
    styleOverrides: {
      actions: {
        display: 'flex',
        gap: 8,
        marginRight: 6,
        [`& .${iconButtonClasses.root}`]: {
          minWidth: 0,
          width: 36,
          height: 36,
        },
      },
    },
  },
  MuiDataGrid: {
    defaultProps: {
      density: 'comfortable',
      rowHeight: 52,
      columnHeaderHeight: 48,
    },
    styleOverrides: {
      root: ({ theme }) => {
        const isDark = theme.palette.mode === 'dark';
        const borderColor = alpha(theme.palette.divider, isDark ? 0.6 : 0.3);
        const hoverColor = alpha(theme.palette.primary.main, isDark ? 0.16 : 0.08);
        const headerBg = alpha(theme.palette.primary.main, isDark ? 0.22 : 0.06);
        const zebraColor = alpha(theme.palette.primary.main, isDark ? 0.18 : 0.04);

        return {
          border: `1px solid ${borderColor}`,
          borderRadius: theme.spacing(2),
          backgroundColor: theme.palette.background.paper,
          boxShadow: isDark
            ? '0 12px 40px rgba(0,0,0,0.45)'
            : '0 24px 45px rgba(15, 23, 42, 0.08)',
          overflow: 'hidden',
          '--DataGrid-cellBorderWidth': '0',
          '--DataGrid-rowBorderColor': borderColor,
          '--DataGrid-containerBackground': theme.palette.background.paper,

          '& .MuiDataGrid-overlay': {
            backgroundColor: alpha(theme.palette.background.default, isDark ? 0.7 : 0.85),
            backdropFilter: 'blur(6px)',
          },

          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: headerBg,
            backdropFilter: 'blur(4px)',
            borderBottom: `1px solid ${borderColor}`,
            overflow: 'hidden',
            // Hide any native scrollbar that appears in the header area
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
          },

          '& .MuiDataGrid-columnHeader': {
            paddingInline: theme.spacing(2),
            fontWeight: 600,
            letterSpacing: 0.3,
            textTransform: 'uppercase',
            color: theme.palette.text.secondary,
            '&:focus, &:focus-within': {
              outline: 'none',
            },
            '&.MuiDataGrid-columnHeader--sorted': {
              color: theme.palette.text.primary,
            },
          },

          '& .MuiDataGrid-columnSeparator': {
            display: 'none',
          },

          '& .MuiDataGrid-cell': {
            borderBottom: `1px solid ${alpha(borderColor, 0.5)}`,
            color: theme.palette.text.primary,
            fontSize: theme.typography.body2.fontSize,
            paddingInline: theme.spacing(2),
            transition: 'background-color 160ms ease, color 160ms ease',
            '&:focus, &:focus-within': {
              outline: 'none',
            },
            '&.MuiDataGrid-cell--editing': {
              backgroundColor: alpha(theme.palette.warning.main, 0.14),
              boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.warning.main, 0.4)}`,
            },
          },

          '& .MuiDataGrid-row': {
            transition: 'transform 160ms ease, background-color 160ms ease',
            '&:hover': {
              backgroundColor: hoverColor,
              transform: 'translate3d(0, -1px, 0)',
            },
            '&.Mui-selected': {
              backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.28 : 0.12),
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.32 : 0.18),
              },
            },
            '&:nth-of-type(2n)': {
              backgroundColor: zebraColor,
            },
          },

          '& .MuiDataGrid-footerContainer': {
            borderTop: `1px solid ${borderColor}`,
            backgroundColor: alpha(theme.palette.background.paper, isDark ? 0.9 : 0.95),
            backdropFilter: 'blur(4px)',
            padding: theme.spacing(0, 2),
            '& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel': {
              fontWeight: 500,
              letterSpacing: 0.2,
            },
          },

          '& .MuiDataGrid-toolbarContainer': {
            padding: theme.spacing(1.5, 2),
            gap: theme.spacing(1),
            borderBottom: `1px solid ${borderColor}`,
            backgroundColor: alpha(theme.palette.background.paper, isDark ? 0.85 : 0.92),
            '& .MuiButton-root': {
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: theme.shape.borderRadius,
            },
          },

          // ─── Scrollbar theming ──────────────────────────────────────────
          // Hide the native scrollbar on virtualScroller; MUI DataGrid renders
          // its own overlay scrollbar elements (.MuiDataGrid-scrollbar--vertical /
          // --horizontal) that we style below.
          '& .MuiDataGrid-virtualScroller': {
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          },

          // MUI overlay vertical scrollbar:
          // By default it starts at top:0 (covering the column headers).
          // Push it down by the header height so it only appears in the data area.
          '& .MuiDataGrid-scrollbar--vertical': {
            top: 'var(--DataGrid-headersTotalHeight)',
            height: 'calc(100% - var(--DataGrid-headersTotalHeight))',
            width: 6,
            // Firefox
            scrollbarWidth: 'thin',
            scrollbarColor: `${isDark ? gray[600] : gray[400]} transparent`,
            // Webkit
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              background: isDark ? gray[700] : gray[300],
              borderRadius: 3,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: isDark ? gray[500] : gray[400],
            },
            '&::-webkit-scrollbar-corner': { background: 'transparent' },
          },

          // MUI overlay horizontal scrollbar
          '& .MuiDataGrid-scrollbar--horizontal': {
            height: 6,
            scrollbarWidth: 'thin',
            scrollbarColor: `${isDark ? gray[600] : gray[400]} transparent`,
            '&::-webkit-scrollbar': { height: 6 },
            '&::-webkit-scrollbar-track': { background: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              background: isDark ? gray[700] : gray[300],
              borderRadius: 3,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: isDark ? gray[500] : gray[400],
            },
          },
        };
      },
    },
  },
  MuiIcon: {
    defaultProps: {
      fontSize: 'small',
    },
    styleOverrides: {
      root: {
        variants: [
          {
            props: {
              fontSize: 'small',
            },
            style: {
              fontSize: '1rem',
            },
          },
        ],
      },
    },
  },
};
